/**
 * The standing brief, as the tune panel lists it under "Standing brief · N of 12":
 * one row per directive, its words in full, and two presses — Edit, which rewords
 * it in place, and ✕.
 *
 * The list was a row of chips with a ✕ each, so a directive could be taken out
 * and typed again, and nothing else: the retyped one went to the end of the list
 * and took the day it was retyped as the day it joined, so the report dated a
 * given the reader had held since the first pass to the afternoon they fixed a
 * word in it. An edit keeps its place. What it does to the date is the store's
 * one writer's to decide (`briefWrite`): the words are dated, because the passes
 * before an edit were sent the old ones, and those go beside the brief with their
 * own dates so the thread and the report can say so.
 *
 * Each row also says what the chips kept in a hover: the gap a directive answers,
 * and — where passes had run before it — "added 14 Sep, after 2 passes", the
 * report's own note (`briefLateNote`), read here in the reader's short day.
 *
 * Every press here is `setBrief`: free, one step of history, and no model call
 * (invariant 9). Nothing is queued, so the lock never refuses it.
 */

import { useEffect, useRef, useState } from 'react'
import {
  DIRECTIVE_CHARS,
  briefEditedToast,
  briefLateNote,
  directiveLine,
  reworded,
} from '../core/brief'
import { shortDay } from '../core/dates'
import { useStore } from '../core/store'
import { DIRECTIVE_LABEL, type Directive, type Session } from '../core/types'
import { Icon } from './components/Icon'
import { useEscapeToClose } from './components/useEscapeToClose'

/** `session` is the tune panel's own read of the store, handed down so the list
 *  renders what the panel around it does (and so a static render can hold it). */
export function BriefList({ session }: { session: Session }) {
  const setBrief = useStore((s) => s.setBrief)
  const toast = useStore((s) => s.toast)
  const brief = session.brief ?? []

  // The directive being reworded, held by identity rather than by index: an undo,
  // or a gap's "Take it back" on the card, can move it while the field is open,
  // and Save must never reword the neighbour that slid into its place.
  const [editing, setEditing] = useState<Directive | null>(null)
  const [words, setWords] = useState('')
  const open = editing !== null && brief.includes(editing) ? editing : null

  // Focus lands on what replaced the press (D217): the row's Edit once its field
  // is put down, saved or not, and the row that slid into the place of one
  // removed. By index, because the row a press re-renders is a new element.
  const presses = useRef(new Map<number, HTMLButtonElement>())
  const [refocus, setRefocus] = useState<number | null>(null)
  useEffect(() => {
    if (refocus === null) return
    presses.current.get(refocus)?.focus()
    setRefocus(null)
  }, [refocus])
  const putDown = () => {
    if (open) setRefocus(brief.indexOf(open))
    setEditing(null)
  }

  // Escape puts the field down and leaves the panel open: the panel is the layer
  // under it, and the ladder's next rung would close it with the words unsaved.
  useEscapeToClose(open !== null, putDown)

  const save = () => {
    if (!open) return
    const i = brief.indexOf(open)
    const next = reworded(brief, i, words)
    putDown()
    if (!next) return
    const now = next[i]!
    const was = open.text
    setBrief(next)
    toast(briefEditedToast(now), 'info', {
      label: 'Undo',
      run: () => {
        // Exactly the directive this press wrote, wherever it now sits, back to
        // the words it had — never "the last step", which may be somebody else's.
        const cur = useStore.getState()
        const list = cur.session.brief ?? []
        const j = list.findIndex((d) => d.kind === now.kind && d.text === now.text)
        const back = j >= 0 ? reworded(list, j, was) : null
        if (back) cur.setBrief(back)
      },
    })
  }

  return (
    <ul className="brieflist" aria-label="Standing brief">
      {brief.map((d, i) => {
        const gap = d.answers ? session.nodes[d.answers] : undefined
        const late = briefLateNote(session, d, (t) => shortDay(t))
        const note = [gap ? `your answer to “${gap.label}”` : null, late]
          .filter(Boolean)
          .join(' · ')
        const line = directiveLine(d)
        return (
          <li key={i} className="brieflist-row">
            {open === d ? (
              <form
                className="brieflist-edit"
                onSubmit={(e) => {
                  e.preventDefault()
                  save()
                }}
              >
                <span className="brieflist-kind">{DIRECTIVE_LABEL[d.kind]}</span>
                {/* Two lines, the row's full width: a directive runs to 240
                    characters, and a one-line field showed the end of it. ↵
                    saves — a directive is one line — and Escape puts it down. */}
                <textarea
                  className="textarea"
                  rows={2}
                  value={words}
                  maxLength={DIRECTIVE_CHARS}
                  autoFocus
                  aria-label={`Reword ${line}`}
                  onChange={(e) => setWords(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
                    e.preventDefault()
                    save()
                  }}
                />
                <span className="brieflist-acts">
                  <button
                    type="submit"
                    className="btn btn--outline"
                    disabled={reworded(brief, i, words) === null}
                  >
                    Save
                  </button>
                  <button type="button" className="btn" onClick={putDown}>
                    Cancel
                  </button>
                </span>
              </form>
            ) : (
              <>
                <span className="brieflist-text">
                  <span className="brieflist-kind">{DIRECTIVE_LABEL[d.kind]}:</span> {d.text}
                </span>
                <span className="brieflist-acts">
                  <button
                    ref={(el) => {
                      if (el) presses.current.set(i, el)
                      else presses.current.delete(i)
                    }}
                    type="button"
                    className="btn brieflist-press"
                    aria-label={`Edit ${line}`}
                    title="Reword it in place. The passes already made keep the words they were sent. No model call; undoable."
                    onClick={() => {
                      setEditing(d)
                      setWords(d.text)
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn--icon brieflist-press"
                    aria-label={`Remove ${line}`}
                    title="Take it out of the brief. No model call; undoable."
                    onClick={() => {
                      setBrief(brief.filter((x) => x !== d))
                      setRefocus(Math.min(i, brief.length - 2))
                    }}
                  >
                    <Icon name="close" size={11} />
                  </button>
                </span>
              </>
            )}
            {note && <span className="brieflist-note">{note}</span>}
          </li>
        )
      })}
    </ul>
  )
}
